import React from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationOutlined,
  InfoOutlined,
  LineChartOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import CardRow from 'containers/Overwatch/components/CardRow';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { orbitType } from '../../styles';
import { formatSupportDate, formatSupportDateTime } from '../supportFormat';
import {
  ActionRowBlock,
  BannerBlock,
  BulletListBlock,
  ChartBlock,
  ConfidenceBlock,
  KeyValueBlock,
  MetricTilesBlock,
  SupportColumnKind,
  SupportConfidenceLevel,
  SupportTicketStatus,
  SupportTone,
  SupportValueTone,
  TableBlock,
  TextBlock
} from '../types';
import SupportChart, { formatSupportValue, hasChartData } from './SupportChart';
import {
  CompactTable,
  CtaRow,
  KeyValueRow,
  Pill,
  RowTitle,
  Stack,
  StatusText,
  SupportActionButton,
  TitleCell,
  sp,
  VisuallyHidden
} from './supportStyles';

const T = `${TOrbit}.support`;

const VALUE_TONE: Record<SupportValueTone, string> = {
  default: sp.text,
  positive: sp.success,
  negative: sp.danger,
  warning: sp.warning,
  info: sp.link
};

const BANNER_TONE: Record<SupportTone, { color: string; Icon: React.ComponentType }> = {
  success: { color: sp.success, Icon: CheckOutlined },
  warning: { color: sp.warning, Icon: ExclamationOutlined },
  error: { color: sp.danger, Icon: CloseOutlined },
  info: { color: sp.link, Icon: InfoOutlined }
};

const CONFIDENCE_COLOR: Record<SupportConfidenceLevel, string> = {
  high: sp.success,
  medium: sp.warning,
  low: sp.danger
};

const CONFIDENCE_LABEL: Record<SupportConfidenceLevel, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence'
};

const STATUSES: ReadonlySet<string> = new Set<SupportTicketStatus>([
  'open',
  'pending',
  'investigating',
  'resolved',
  'closed'
]);

const Prose = styled.p`
  margin: 0;
  color: ${sp.muted};
  ${orbitType.conversation}
`;

const Highlight = styled.strong`
  color: ${sp.text};
  font-weight: 700;
`;

/** Splits `text` around each highlight, matched verbatim, so no markup ever comes from the wire. */
const highlightRuns = (text: string, terms: string[] = []): React.ReactNode[] => {
  const usable = terms.filter(term => term !== '');
  if (usable.length === 0) {
    return [text];
  }
  const escaped = usable.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return text.split(new RegExp(`(${escaped.join('|')})`)).map((part, index) =>
    usable.includes(part) ? (
      // Runs carry no id; their position in the sentence is their identity.
      // eslint-disable-next-line react/no-array-index-key
      <Highlight key={index} data-testid="orbit-support-highlight">
        {part}
      </Highlight>
    ) : (
      part
    )
  );
};

export const Text: React.FC<{ block: TextBlock }> = ({ block }) => (
  <Prose>{highlightRuns(block.text, block.highlights)}</Prose>
);

export const Chart: React.FC<{ block: ChartBlock }> = ({ block }) => {
  if (!hasChartData(block.chart)) {
    return block.fallback_text ? <Prose>{block.fallback_text}</Prose> : null;
  }
  return <SupportChart chart={block.chart} title={block.title} />;
};

const Titled = styled(RowTitle)`
  margin-bottom: 6px;
`;

export const KeyValue: React.FC<{ block: KeyValueBlock }> = ({ block }) => (
  <div>
    {block.title ? <Titled>{block.title}</Titled> : null}
    {block.rows.map(row => (
      <KeyValueRow key={row.label}>
        <span>{row.label}</span>
        <span
          data-tone={row.tone ?? 'default'}
          style={{ color: VALUE_TONE[row.tone ?? 'default'] }}
        >
          {row.value}
        </span>
      </KeyValueRow>
    ))}
    {block.actions?.length ? (
      <CtaRow>
        {block.actions.map(action => (
          <SupportActionButton key={action.id} action={action} />
        ))}
      </CtaRow>
    ) : null}
  </div>
);

const TableCell: React.FC<{
  kind: SupportColumnKind;
  value: string | number | null | undefined;
  currency?: string | null;
}> = ({ kind, value, currency }) => {
  const { t } = useMultiLanguageTranslation();
  if (value === null || value === undefined || value === '') {
    return <td>—</td>;
  }
  const text = String(value);
  switch (kind) {
    case 'text':
      return <TitleCell title={text} />;
    case 'number':
    case 'percent':
    case 'currency':
      return (
        <td>{formatSupportValue(Number(value), kind === 'number' ? 'count' : kind, currency)}</td>
      );
    case 'date':
      return <td>{formatSupportDate(text)}</td>;
    case 'datetime':
      return <td>{formatSupportDateTime(text)}</td>;
    case 'priority':
      return (
        <td>
          <Pill $color={sp.warning}>{text}</Pill>
        </td>
      );
    case 'status':
      return STATUSES.has(text) ? (
        <td>
          <StatusText $status={text as SupportTicketStatus}>{t(`${T}.status.${text}`)}</StatusText>
        </td>
      ) : (
        <td>{text}</td>
      );
    default:
      return <td>{text}</td>;
  }
};

export const Table: React.FC<{ block: TableBlock }> = ({ block }) => (
  <div>
    {block.title ? <Titled>{block.title}</Titled> : null}
    <CompactTable>
      <VisuallyHidden>{block.title ?? 'Table'}</VisuallyHidden>
      <thead>
        <tr>
          {block.columns.map(column => (
            <th key={column.key}>{column.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.rows.map((row, index) => (
          // Rows carry no id of their own.
          // eslint-disable-next-line react/no-array-index-key
          <tr key={index}>
            {block.columns.map(column => (
              <TableCell
                key={column.key}
                kind={column.kind}
                value={row[column.key]}
                currency={block.currency}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </CompactTable>
    {block.actions?.length ? (
      <CtaRow>
        {block.actions.map(action => (
          <SupportActionButton key={action.id} action={action} />
        ))}
      </CtaRow>
    ) : null}
  </div>
);

const Tile = styled.div`
  padding: 10px 12px;
  border: 1px solid ${sp.border};
  border-radius: 10px;
  background: ${sp.inset};
`;

const TileLabel = styled.p`
  margin: 0;
  color: ${sp.muted};
  ${orbitType.helping}
`;

const TileValue = styled.p<{ $color: string }>`
  margin: 2px 0 0;
  color: ${({ $color }) => $color};
  ${orbitType.containerTitle}
  font-weight: 700;
`;

export const MetricTiles: React.FC<{ block: MetricTilesBlock }> = ({ block }) => (
  <CardRow $columns={Math.min(block.tiles.length, 4)} $gap={8} $min={120}>
    {block.tiles.map(tile => (
      <Tile key={tile.label} data-testid="orbit-support-tile">
        <TileLabel>{tile.label}</TileLabel>
        <TileValue $color={VALUE_TONE[tile.tone ?? 'default']}>{tile.value}</TileValue>
        {tile.caption ? <TileLabel>{tile.caption}</TileLabel> : null}
      </Tile>
    ))}
  </CardRow>
);

const Bullets = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: ${sp.muted};
  ${orbitType.containerBody}
`;

export const BulletList: React.FC<{ block: BulletListBlock }> = ({ block }) => (
  <div>
    {block.title ? <Titled>{block.title}</Titled> : null}
    <Bullets>
      {block.items.map(item => (
        <li key={item}>{item}</li>
      ))}
    </Bullets>
  </div>
);

export const ActionRow: React.FC<{ block: ActionRowBlock }> = ({ block }) => (
  <CtaRow>
    {block.actions.map(action => (
      <SupportActionButton key={action.id} action={action} />
    ))}
  </CtaRow>
);

const BannerBox = styled.div<{ $color: string }>`
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  border: 1px solid ${({ $color }) => $color};
  border-radius: 14px;
  background: ${({ $color }) => `${$color}24`};
`;

const BannerIcon = styled.span<{ $color: string }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 12px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  color: #ffffff;
`;

const BannerTitle = styled.p<{ $color: string }>`
  margin: 0;
  color: ${({ $color }) => $color};
  ${orbitType.containerTitle}
  font-weight: 700;
`;

const BannerBody = styled.p`
  margin: 4px 0 0;
  color: ${sp.muted};
  ${orbitType.containerBody}
`;

const BannerBullets = styled(Bullets)`
  margin-top: 8px;
`;

export const Banner: React.FC<{ block: BannerBlock }> = ({ block }) => {
  const { color, Icon } = BANNER_TONE[block.tone] ?? BANNER_TONE.info;
  return (
    <BannerBox
      $color={color}
      role={block.tone === 'error' ? 'alert' : 'status'}
      data-tone={block.tone}
    >
      <BannerIcon $color={color} aria-hidden="true">
        <Icon />
      </BannerIcon>
      <Stack $gap={0}>
        <BannerTitle $color={color}>{block.title}</BannerTitle>
        {block.body ? <BannerBody>{block.body}</BannerBody> : null}
        {block.bullets?.length ? (
          <BannerBullets>
            {block.bullets.map(bullet => (
              <li key={bullet}>{bullet}</li>
            ))}
          </BannerBullets>
        ) : null}
        {block.actions?.length ? (
          <CtaRow>
            {block.actions.map(action => (
              <SupportActionButton key={action.id} action={action} />
            ))}
          </CtaRow>
        ) : null}
      </Stack>
    </BannerBox>
  );
};

const ConfidencePill = styled.span<{ $color: string }>`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid ${({ $color }) => $color};
  border-radius: 999px;
  color: ${({ $color }) => $color};
  ${orbitType.containerBody}
  font-weight: 700;

  > * + * {
    margin-left: 6px;
  }
`;

const Reason = styled.p`
  margin: 8px 0 0;
  color: ${sp.text};
  ${orbitType.containerBody}

  > strong {
    font-weight: 700;
  }
`;

const REASON_LEAD = 'Reason:';

export const Confidence: React.FC<{ block: ConfidenceBlock }> = ({ block }) => {
  const color = CONFIDENCE_COLOR[block.level];
  const reason = block.reason?.startsWith(REASON_LEAD)
    ? block.reason.slice(REASON_LEAD.length).trim()
    : block.reason;
  return (
    <Stack $gap={0}>
      <ConfidencePill $color={color}>
        <SafetyCertificateOutlined aria-hidden="true" />
        <span>{`${CONFIDENCE_LABEL[block.level]} · ${block.score}%`}</span>
      </ConfidencePill>
      {reason ? (
        <Reason>
          <strong>{REASON_LEAD}</strong> {reason}
        </Reason>
      ) : null}
    </Stack>
  );
};

const PanelBox = styled.section<{ $info: boolean }>`
  border: 1px solid ${({ $info }) => ($info ? sp.link : sp.border)};
  border-radius: 14px;
  background: ${sp.bubble};
  overflow: hidden;
`;

const PanelHead = styled.header`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${sp.border};
  color: ${sp.link};
  ${orbitType.containerBody}
  font-weight: 700;
  letter-spacing: 0.04em;

  > * + * {
    margin-left: 8px;
  }
`;

const PanelBody = styled(Stack)`
  padding: 16px;
`;

/** The heading + body container; its children go through the same `type` switch. */
export const PanelShell: React.FC<{
  heading: string;
  info: boolean;
  children: React.ReactNode;
}> = ({ heading, info, children }) => (
  <PanelBox $info={info}>
    <PanelHead>
      <LineChartOutlined aria-hidden="true" />
      <span>{heading}</span>
    </PanelHead>
    <PanelBody $gap={14}>{children}</PanelBody>
  </PanelBox>
);
